import {
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/form-control'

import { useController, FieldValues, UseControllerProps } from 'react-hook-form'

import { Select as ChakraSelect, Props, GroupBase } from 'chakra-react-select'

import { useTranslation } from 'react-i18next'

interface SelectProps<
  FormValues extends FieldValues = FieldValues,
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<Props<Option, IsMulti, Group>, 'name' | 'defaultValue'>,
    UseControllerProps<FormValues> {
  label?: string
}

export function Select<
  FormValues extends FieldValues = FieldValues,
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  label,
  options,
  control,
  rules,
  shouldUnregister,
  ...selectProps
}: SelectProps<FormValues, Option, IsMulti, Group>) {
  const { t } = useTranslation()

  const {
    field,
    fieldState: { error },
  } = useController<FormValues>({
    name,
    control,
    rules,
    shouldUnregister,
  })

  return (
    <FormControl isInvalid={!!error} zIndex={99}>
      {!!label && (
        <FormLabel htmlFor={name} fontSize="sm">
          {label}
        </FormLabel>
      )}
      <ChakraSelect
        id={name}
        options={options}
        chakraStyles={{
          control: provided => ({
            ...provided,
            borderColor: '#CBCBCB',
            _focus: {
              borderColor: 'parkoff.orange.300',
              borderWidth: '2px',
            },
          }),
          dropdownIndicator: provided => ({
            ...provided,
            background: 'transparent',
          }),
          placeholder: provided => ({
            ...provided,
            fontSize: 'sm',
          }),
          valueContainer: provided => ({
            ...provided,
            fontSize: 'sm',
          }),
          option: provided => ({
            ...provided,
            fontSize: 'sm',
          }),
        }}
        {...selectProps}
        {...field}
      />
      {!!error && (
        <FormErrorMessage role="alert">{t(error.message!)}</FormErrorMessage>
      )}
    </FormControl>
  )
}
